import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Star, Quote, Sparkles } from 'lucide-react';
import { useSiteContent, DEFAULT_LANDING_REVIEWS } from '../../lib/siteContent';
import { LandingSectionShell } from './LandingSectionShell';

export default function Reviews() {
  const { content } = useSiteContent();
  const reviews = content.landingReviews.length > 0 ? content.landingReviews : DEFAULT_LANDING_REVIEWS;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headerRef = useRef(null);
  const inView = useInView(headerRef, { once: true, margin: '-40px' });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) entry.target.classList.add('section-visible');
      },
      { threshold: 0.1 }
    );
    if (headingRef.current) observer.observe(headingRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <LandingSectionShell id="stories" className="!overflow-hidden">
      <div ref={headerRef} className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          className="mx-auto mb-4 flex justify-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">
            <Sparkles className="h-3 w-3 text-emerald-400" />
            Customer stories
          </span>
        </motion.div>

        <h2 ref={headingRef} className="section-heading-saas--on-dark">
          Trusted by finance shops across India
        </h2>
        <p className="mx-auto mb-12 max-w-2xl text-center text-slate-400">
          Owners and collections teams run multi-branch operations on EdgeFinances every day.
        </p>

        <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-[#030712] to-transparent sm:w-28" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-[#030712] to-transparent sm:w-28" />
          <div className="flex w-max gap-5 pb-2 pt-1 animate-scroll hover:[animation-play-state:paused]">
            {[...reviews, ...reviews].map((review, index) => (
              <motion.div
                key={`${review.name}-${index}`}
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 280, damping: 18 }}
                className="landing-glow-ring w-[min(100vw-2rem,400px)] shrink-0 rounded-2xl p-[1px]"
                style={{ transformStyle: 'preserve-3d', perspective: 1200 }}
              >
                <div className="landing-glass-panel--static h-full rounded-2xl p-7">
                  <Quote className="mb-4 h-9 w-9 text-emerald-500/35" aria-hidden />
                  <div className="mb-4 flex gap-0.5">
                    {[...Array(Math.max(0, Math.min(5, review.rating)))].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-400/90 text-amber-400/90" />
                    ))}
                  </div>
                  <p className="mb-6 text-sm leading-relaxed text-slate-300">&ldquo;{review.review}&rdquo;</p>
                  <div className="flex items-center gap-3 border-t border-white/[0.06] pt-5">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-emerald-600/40 to-teal-600/25 text-sm font-bold text-emerald-100 ring-2 ring-emerald-500/30">
                      {review.name.trim().slice(0, 1) || '?'}
                    </div>
                    <div>
                      <p className="font-display text-sm font-semibold text-white">{review.name}</p>
                      <p className="text-xs text-slate-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </LandingSectionShell>
  );
}
